import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import {
  ArrowLeft,
  Sword,
  Users,
  Trophy,
  Search,
  Plus,
  Crown,
  Target,
  Clock,
  Star,
  Shield,
  Heart,
  Skull,
  DollarSign,
  UserPlus,
  Check,
  X,
  Eye,
  Archive,
  RefreshCw,
  AlertCircle,
  MessageSquare,
  Camera,
  TrendingUp,
  Bell,
  BellOff,
  LogIn,
  UserCircle,
  ChevronDown,
  Trash2,
  History,
  BarChart3,
  Coins,
  AlertTriangle,
  Swords
} from "lucide-react";
import BountyPoster from "../components/BountyPoster";
import ExpeditionPoster from "../components/ExpeditionPoster";
import CreateBountyModal from "../components/modals/CreateBountyModal";
import CreateExpeditionModal from "../components/modals/CreateExpeditionModal";
import CreateWishModal from "../components/modals/CreateWishModal";
import CreateHunterLFGModal from "../components/modals/CreateHunterLFGModal";
import ApplyExpeditionModal from "../components/modals/ApplyExpeditionModal";
import ApplyClientModal from "../components/modals/ApplyClientModal";
import ConfirmCompletionModal from "../components/modals/ConfirmCompletionModal";
import UserProfileModal from "../components/UserProfileModal";
import KofiModal from "../components/KofiModal";

import FightNotificationContainer from "../components/FightNotification";
import FightNotificationLog from "../components/FightNotificationLog";
import Shoutbox from "../components/Shoutbox";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Parchment background URL - provided by user
const PARCHMENT_BG = "https://customer-assets.emergentagent.com/job_bounty-hub-5/artifacts/9qj2hqka_Gemini_Generated_Image_w0kodsw0kodsw0ko.png";

// Boss image mapping for area bosses (bounties)
const BOSS_IMAGE_MAPPING = {
  "Mano": "https://legends.ml/static/images/lib/monster/2220000.png",
  "Stumpy": "https://legends.ml/static/images/lib/monster/3220000.png",
  "Deo": "https://legends.ml/static/images/lib/monster/3220001.png",
  "King Clang": "https://legends.ml/static/images/lib/monster/5220000.png",
  "Seruf": "https://legends.ml/static/images/lib/monster/4220001.png",
  "Faust": "https://legends.ml/static/images/lib/monster/5220002.png",
  "Giant Centipede": "https://legends.ml/static/images/lib/monster/9500177.png",
  "Timer": "https://legends.ml/static/images/lib/monster/5220003.png",
  "Mushmom": "https://legends.ml/static/images/lib/monster/6130101.png",
  "Dyle": "https://legends.ml/static/images/lib/monster/6220000.png",
  "Zombie Mushmom": "https://legends.ml/static/images/lib/monster/6300005.png",
  "Zeno": "https://legends.ml/static/images/lib/monster/6220001.png",
  "Nine-Tailed Fox": "https://legends.ml/static/images/lib/monster/7220001.png",
  "Tae Roon": "https://legends.ml/static/images/lib/monster/7220000.png",
  "King Sage Cat": "https://legends.ml/static/images/lib/monster/7220002.png",
  "Jr. Balrog": "https://legends.ml/static/images/lib/monster/8130100.png",
  "Eliza": "https://legends.ml/static/images/lib/monster/8220000.png",
  "Snack Bar": "https://legends.ml/static/images/lib/monster/9410015.png",
  "Chimera": "https://legends.ml/static/images/lib/monster/8220002.png",
  "Blue Mushmom": "https://legends.ml/static/images/lib/monster/9400205.png",
  "Snowman": "https://legends.ml/static/images/lib/monster/8220001.png",
  "Headless Horseman": "https://legends.ml/static/images/lib/monster/9400549.png",
  "Manon": "https://legends.ml/static/images/lib/monster/9500174.png",
  "Griffey": "https://legends.ml/static/images/lib/monster/8180001.png",
  "Pianus (16h)": "https://legends.ml/static/images/lib/monster/8510000.png",
  "Pianus (24h)": "https://legends.ml/static/images/lib/monster/8520000.png",
  "BigFoot": "https://legends.ml/static/images/lib/monster/9400575.png",
  "Black Crow": "https://legends.ml/static/images/lib/monster/9400014.png",
  "Leviathan": "https://legends.ml/static/images/lib/monster/8220003.png",
  "Kacchuu Musha": "https://legends.ml/static/images/lib/monster/9400405.png",
  "Dodo": "https://legends.ml/static/images/lib/monster/8220004.png",
  "Anego / Female Boss": "https://legends.ml/static/images/lib/monster/9400121.png",
  "Lilynouch": "https://legends.ml/static/images/lib/monster/8220005.png",
  "Lyka": "https://legends.ml/static/images/lib/monster/8220006.png",
  "Balrog": "https://royals-library.netlify.app/images/monsters/8830000.png",
  "Capt. Latanica": "https://legends.ml/static/images/lib/monster/9420513.png",
  "Chao": "https://iili.io/Bn9xTsn.png",
  "Ephenia": "https://royals-library.netlify.app/images/monsters/5250007.png",
  "Papulatus": "https://legends.ml/static/images/lib/monster/8500001.png",
  "Crimsonwood Keep Expedition (CWKPQ)": "https://iili.io/BnHY0tj.png",
  "Scarlion": "https://legends.ml/static/images/lib/monster/9420549.png",
  "Targa": "https://legends.ml/static/images/lib/monster/9420544.png",
  "Zakum": "https://legends.ml/static/images/lib/monster/8800000.png",
  "Krexel": "https://iili.io/Bn9cSUP.png",
  "Wu-Ling Yaoseng (Shaolin)": "https://legends.ml/static/images/lib/monster/9600025.png",
  "Horntail": "https://legends.ml/static/images/lib/monster/8810018.png",
  "The Boss Expedition": "https://legends.ml/static/images/lib/monster/9400300.png",
  "Castellan Toad": "https://legends.ml/static/images/lib/monster/9400409.png",
  "Vergamot": "https://royals-library.netlify.app/images/monsters/9400263.png",
  "Dunas v1": "https://royals-library.netlify.app/images/monsters/9400270.png",
  "Nibergen": "https://royals-library.netlify.app/images/monsters/9400271.png",
  "Nameless Magic Monster": "https://royals-library.netlify.app/images/monsters/9400266.png",
  "Dunas v2": "https://royals-library.netlify.app/images/monsters/9400270.png",
  "Auf Haven Expedition": "https://royals-library.netlify.app/images/monsters/9400289.png",
  "King Castle Golem": "https://royals-library.netlify.app/images/monsters/8211004.png",
  "Von Leon": "https://royals-library.netlify.app/images/monsters/8840000.png"
};

const getBossImage = (bossName) => {
  const baseName = bossName.split(" - ")[0];
  return BOSS_IMAGE_MAPPING[baseName] || "https://legends.ml/static/images/lib/monster/6130101.png";
};

// Format mesos: input is in millions, display as "X,000,000 mesos"
const formatMesos = (value) => {
  if (!value && value !== 0) return "";
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  const full = num * 1000000;
  return `${full.toLocaleString()} mesos`;
};

// Status badge colors
const getStatusColor = (status) => {
  switch (status) {
    case "ACTIVE": return "bg-green-500";
    case "ASSIGNED": return "bg-blue-500";
    case "SUCCEEDED": return "bg-amber-500";
    case "SETTLED": return "bg-purple-500";
    case "INACTIVE": return "bg-gray-500";
    case "CANCELLED": return "bg-red-500";
    default: return "bg-gray-500";
  }
};

// Area bosses list for bounties (base names only - map doesn't matter for bounties)
const AREA_BOSSES = [
  "Mano", "Stumpy", "Deo", "King Clang", "Seruf",
  "Faust", "Giant Centipede", "Timer", "Mushmom", "Dyle",
  "Zombie Mushmom", "Zeno", "Nine-Tailed Fox", "Tae Roon",
  "King Sage Cat", "Jr. Balrog", "Eliza", "Snack Bar", "Chimera",
  "Blue Mushmom", "Snowman", "Headless Horseman", "Manon", "Griffey",
  "Pianus (16h)", "Pianus (24h)", "BigFoot", "Black Crow", "Leviathan",
  "Kacchuu Musha", "Dodo", "Anego / Female Boss",
  "Lilynouch", "Lyka"
];

const HuntersChancePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token, logout, setShowAuth } = useAuth();
  
  // Tab state - persist in localStorage
  const [mainTab, setMainTab] = useState("client");
  const [subTab, setSubTab] = useState("bounty");

  // Restore tab state from localStorage on mount
  useEffect(() => {
    const savedTab = searchParams.get("tab") || localStorage.getItem("hc_mainTab");
    const savedSub = searchParams.get("sub") || localStorage.getItem("hc_subTab");
    if (savedTab && savedTab !== mainTab) setMainTab(savedTab);
    if (savedSub && savedSub !== subTab) setSubTab(savedSub);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Data state
  const [bounties, setBounties] = useState([]);
  const [myBounties, setMyBounties] = useState([]);
  const [expeditions, setExpeditions] = useState([]);
  const [myExpeditions, setMyExpeditions] = useState([]);
  const [expeditionBosses, setExpeditionBosses] = useState([]);
  const [expeditionWishes, setExpeditionWishes] = useState([]);
  const [hunterLfgPosts, setHunterLfgPosts] = useState([]);
  const [hunterRankings, setHunterRankings] = useState([]);
  const [clientRankings, setClientRankings] = useState([]);
  
  // Loading state
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Modals
  const [showCreateBounty, setShowCreateBounty] = useState(false);
  const [showCreateExpedition, setShowCreateExpedition] = useState(false);
  const [showCreateWish, setShowCreateWish] = useState(false);
  const [showCreateLfg, setShowCreateLfg] = useState(false);
  const [showKofiModal, setShowKofiModal] = useState(false);
  const [showBountyDetail, setShowBountyDetail] = useState(null);
  const [showExpeditionDetail, setShowExpeditionDetail] = useState(null);
  const [showUserProfile, setShowUserProfile] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(null);
  const [showApplyExpedition, setShowApplyExpedition] = useState(null);
  
  // Form state
  const [bountyForm, setBountyForm] = useState({
    boss_name: "",
    reward: "",
    reward_apr_amount: "",
    reward_onyx_apple_amount: "",
    contact_igns: ["", "", ""],
    duration_minutes: 60,
    message: ""
  });
  
  const [expeditionForm, setExpeditionForm] = useState({
    boss_id: "",
    expected_time: "",
    expected_runtime: "",
    price_for_client: "",
    hb_provided: false,
    hp_gear_provided: false,
    hp_gear_value: "",
    safe_run_guaranteed: false,
    selling_service: true,
    apr_required: false,
    apr_amount: "",
    onyx_apple_required: false,
    onyx_apple_amount: "",
    total_slots: 6,
    free_for_all: false,
    job_requirements: [],
    message: "",
    leader_characters: [],
    selected_wish_id: null,
    invite_lfg_ids: []
  });

  // Track pending expedition invites for current user
  const [pendingExpeditionInvites, setPendingExpeditionInvites] = useState([]);

  const [showApplyAsClient, setShowApplyAsClient] = useState(null);
  const [clientApplyIgn, setClientApplyIgn] = useState("");
  
  const [wishForm, setWishForm] = useState({
    boss_id: "",
    reward_offered: "",
    contact_ign: "",
    hb_required: false,
    hp_gear_required: false,
    safe_run_required: false,
    message: "",
    queue_duration_hours: 4
  });

  const [lfgForm, setLfgForm] = useState({
    boss_ids: [],
    selected_characters: [],
    has_hb: false,
    message: "",
    queue_duration_hours: 4
  });
  
  const [applyForm, setApplyForm] = useState({
    selected_characters: [],
    message: ""
  });
  
  const [confirmForm, setConfirmForm] = useState({
    screenshot: null,
    comment: ""
  });
  
  // Jobs data
  const [availableJobs, setAvailableJobs] = useState({});
  const [userCharacters, setUserCharacters] = useState([]);
  
  // Job requirement being added
  const [newJobReq, setNewJobReq] = useState({
    job: "",
    count: 1,
    min_level: ""
  });
  
  // Search/filter
  const [searchTerm, setSearchTerm] = useState("");
  const [expTimeFilter, setExpTimeFilter] = useState("ALL");
  const [expBossFilter, setExpBossFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  
  // My section filters
  const [myWorkStatusFilter, setMyWorkStatusFilter] = useState("ALL");
  const [myBountyStatusFilter, setMyBountyStatusFilter] = useState("ALL");
  const [myExpStatusFilter, setMyExpStatusFilter] = useState("ALL");
  
  // Notification bell state
  const [bellNotifications, setBellNotifications] = useState({ total: 0, hunter_chance_count: 0, hunter_chance_notifications: [] });
  
  // Fight notifications
  const [fightNotifications, setFightNotifications] = useState([]);
  const [fightNotificationHistory, setFightNotificationHistory] = useState(() => {
    const saved = localStorage.getItem("hcFightNotificationHistory");
    return saved ? JSON.parse(saved) : [];
  });
  const [showFightLog, setShowFightLog] = useState(false);
  const [hcNotificationsEnabled, setHcNotificationsEnabled] = useState(() => {
    return localStorage.getItem("hcNotificationsEnabled") !== "false";
  });
  const seenNotificationIds = useRef(new Set());
  
  // Sync tab changes to URL params + localStorage
  const handleMainTabChange = (tab) => {
    setMainTab(tab);
    localStorage.setItem("hc_mainTab", tab);
    setSearchParams({ tab, sub: subTab }, { replace: true });
  };
  const handleSubTabChange = (sub) => {
    setSubTab(sub);
    localStorage.setItem("hc_subTab", sub);
    setSearchParams({ tab: mainTab, sub }, { replace: true });
  };
  
  // Poll notifications
  useEffect(() => {
    if (!user) return;
    const fetchNotifs = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/notifications`);
        setBellNotifications(res.data);
      } catch {}
    };
    fetchNotifs();
    const iv = setInterval(fetchNotifs, 10000);
    return () => clearInterval(iv);
  }, [user]);
  
  // Poll fight notifications for global feed (bounty/expedition updates)
  useEffect(() => {
    if (!hcNotificationsEnabled) {
      setFightNotifications([]);
      return;
    }
    const fetchFight = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/fight/notifications?tracker_type=global`);
        const allNotifs = res.data.notifications || [];
        // Filter to only HC-related notifications
        const hcNotifs = allNotifs.filter(n => 
          n.type?.includes("bounty") || n.type?.includes("expedition") || 
          n.type === "new_bounty" || n.type === "new_expedition"
        );
        
        // Filter out already seen notifications
        const newNotifs = hcNotifs.filter(n => !seenNotificationIds.current.has(n.id));
        
        if (newNotifs.length > 0) {
          newNotifs.forEach(n => seenNotificationIds.current.add(n.id));
          setFightNotifications(prev => [...newNotifs, ...prev].slice(0, 4));
          setFightNotificationHistory(prev => {
            const updated = [...newNotifs, ...prev].slice(0, 50);
            localStorage.setItem("hcFightNotificationHistory", JSON.stringify(updated));
            return updated;
          });
        }
      } catch {}
    };
    fetchFight();
    const iv = setInterval(fetchFight, 5000);
    return () => clearInterval(iv);
  }, [hcNotificationsEnabled]);
  
  // Toggle HC notifications
  const toggleHcNotifications = useCallback(() => {
    setHcNotificationsEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem("hcNotificationsEnabled", newValue.toString());
      if (!newValue) {
        setFightNotifications([]);
      }
      return newValue;
    });
  }, []);
  
  // Dismiss fight notification
  const dismissFightNotification = useCallback((notificationId) => {
    setFightNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);
  
  // Delete from log
  const handleDeleteLog = useCallback((notificationId) => {
    setFightNotificationHistory(prev => {
      const updated = prev.filter(n => n.id !== notificationId);
      localStorage.setItem("hcFightNotificationHistory", JSON.stringify(updated));
      return updated;
    });
  }, []);
  
  // Clear all logs
  const handleClearAllLogs = useCallback(() => {
    setFightNotificationHistory([]);
    localStorage.setItem("hcFightNotificationHistory", "[]");
  }, []);
  
  // Clear all bell notifications
  const clearAllBellNotifications = async () => {
    try {
      await axios.delete(`${API_URL}/api/notifications/hunter-chance`);
      setBellNotifications(prev => ({
        ...prev,
        hunter_chance_notifications: [],
        hunter_chance_count: 0,
        total: prev.total - (prev.hunter_chance_notifications?.length || 0)
      }));
    } catch (error) {
      console.error("Failed to clear notifications", error);
    }
  };
  
  // Clear single bell notification
  const clearSingleBellNotification = async (notifId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/api/fight/notifications/${notifId}`);
      setBellNotifications(prev => ({
        ...prev,
        hunter_chance_notifications: prev.hunter_chance_notifications?.filter(n => n.id !== notifId) || [],
        hunter_chance_count: Math.max(0, prev.hunter_chance_count - 1),
        total: Math.max(0, prev.total - 1)
      }));
    } catch (error) {
      console.error("Failed to clear notification", error);
    }
  };
  
  // Get description for bell notification
  const getBellNotificationDescription = (notif) => {
    const type = notif.type;
    const username = notif.username || "Someone";
    const bossName = notif.boss_name || "boss";
    
    switch(type) {
      case "bounty_accepted": return `${username} accepted your bounty for ${bossName}`;
      case "bounty_confirm_pending": return `${username} completed bounty - confirm for ${bossName}`;
      case "bounty_completed": return `Bounty completed for ${bossName}!`;
      case "expedition_application": return `${username} applied to join your ${bossName} expedition`;
      case "expedition_client_application": return `${username} wants to be buyer for ${bossName}`;
      case "expedition_accepted": return `Accepted to ${bossName} expedition by ${username}`;
      case "expedition_rejected": return `Your ${bossName} application was rejected`;
      case "expedition_client_selected": return `Selected as buyer for ${bossName}`;
      case "expedition_client_confirmed": return `Buyer confirmed for ${bossName} expedition`;
      case "expedition_invite": return `${username} invited you to ${bossName} expedition`;
      case "expedition_expelled": return `Removed from ${bossName} expedition`;
      default: return `Update for ${bossName}`;
    }
  };
  
  const loadData = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      // Parallel load all public data
      const [bossRes, jobsRes, bountyRes, expRes, wishRes, lfgRes, hunterRankRes, clientRankRes] = await Promise.all([
        axios.get(`${API_URL}/api/expedition-bosses`),
        axios.get(`${API_URL}/api/jobs`),
        axios.get(`${API_URL}/api/bounties?status=${statusFilter}`),
        axios.get(`${API_URL}/api/expeditions`),
        axios.get(`${API_URL}/api/expeditions/wishes`),
        axios.get(`${API_URL}/api/expeditions/lfg`),
        axios.get(`${API_URL}/api/reputation/rankings?reputation_type=hunter`),
        axios.get(`${API_URL}/api/reputation/rankings?reputation_type=client`)
      ]);
      
      setExpeditionBosses(bossRes.data.bosses);
      setAvailableJobs(jobsRes.data.jobs || {});
      setBounties(bountyRes.data.bounties);
      setExpeditions(expRes.data.expeditions);
      setExpeditionWishes(wishRes.data.wishes);
      setHunterLfgPosts(lfgRes.data.lfg_posts || []);
      setHunterRankings(hunterRankRes.data.rankings);
      setClientRankings(clientRankRes.data.rankings);
      
      // Load user-specific data in parallel if logged in
      if (user) {
        const authHeaders = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
        const [myBountyRes, myExpRes, charsRes, invitesRes] = await Promise.all([
          axios.get(`${API_URL}/api/bounties/my`, authHeaders),
          axios.get(`${API_URL}/api/expeditions/my`, authHeaders),
          axios.get(`${API_URL}/api/user/characters`, authHeaders).catch(() => ({ data: { characters: [] } })),
          axios.get(`${API_URL}/api/expeditions/my-pending-invites`, authHeaders).catch(() => ({ data: { invites: [] } }))
        ]);
        setMyBounties(myBountyRes.data.bounties);
        setMyExpeditions(myExpRes.data.expeditions);
        setUserCharacters(charsRes.data.characters || []);
        setPendingExpeditionInvites(invitesRes.data.invites || []);
      }
    } catch (error) {
      if (!isBackground) {
        console.error("Error loading data:", error);
        toast.error("Failed to load data");
      }
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [user, statusFilter]);

  // Load initial data
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 15 seconds (background — no loading spinner)
  useEffect(() => {
    const interval = setInterval(() => {
      loadData(true);
    }, 15000);
    return () => clearInterval(interval);
  }, [loadData]);
  
  // Create bounty
  const handleCreateBounty = async () => {
    if (actionLoading) return;
    if (!user) {
      toast.error("Please log in to create a bounty");
      return;
    }
    
    const contactIgns = bountyForm.contact_igns.filter(ign => ign.trim() !== "");
    if (contactIgns.length === 0) {
      toast.error("Please provide at least one contact IGN");
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/bounties`, {
        boss_name: bountyForm.boss_name,
        reward: bountyForm.reward,
        reward_apr_amount: bountyForm.reward_apr_amount ? parseInt(bountyForm.reward_apr_amount) : null,
        reward_onyx_apple_amount: bountyForm.reward_onyx_apple_amount ? parseInt(bountyForm.reward_onyx_apple_amount) : null,
        contact_igns: contactIgns,
        duration_minutes: bountyForm.duration_minutes,
        message: bountyForm.message || null
      });
      
      toast.success("Bounty created successfully!");
      setShowCreateBounty(false);
      setBountyForm({ boss_name: "", reward: "", reward_apr_amount: "", reward_onyx_apple_amount: "", contact_igns: ["", "", ""], duration_minutes: 60, message: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create bounty");
    } finally {
      setActionLoading(false);
    }
  };
  
  // Accept bounty (hunter)
  const handleAcceptBounty = async (bountyId) => {
    if (!user) {
      toast.error("Please log in to accept a bounty");
      return;
    }
    
    try {
      await axios.put(`${API_URL}/api/bounties/${bountyId}/assign`);
      toast.success("Bounty accepted! Contact the buyer.");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept bounty");
    }
  };
  
  // Cancel bounty
  const handleCancelBounty = async (bountyId) => {
    try {
      await axios.put(`${API_URL}/api/bounties/${bountyId}/cancel`);
      toast.success("Bounty cancelled");
      loadData();
      setShowBountyDetail(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel bounty");
    }
  };
  
  // Restore bounty
  const handleRestoreBounty = async (bountyId) => {
    try {
      await axios.put(`${API_URL}/api/bounties/${bountyId}/restore?duration_minutes=60`);
      toast.success("Bounty restored!");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore bounty");
    }
  };
  
  // Confirm bounty completion
  const handleConfirmBounty = async (bountyId) => {
    try {
      await axios.put(`${API_URL}/api/bounties/${bountyId}/confirm`, {
        screenshot: confirmForm.screenshot,
        comment: confirmForm.comment
      });
      toast.success("Confirmation recorded!");
      setShowConfirmDialog(null);
      setConfirmForm({ screenshot: null, comment: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm");
    }
  };
  
  // Report bounty issue
  const handleReportBounty = async (bountyId, reportType) => {
    try {
      await axios.post(`${API_URL}/api/bounties/${bountyId}/report`, {
        bounty_id: bountyId,
        report_type: reportType
      });
      toast.success("Report submitted");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit report");
    }
  };
  
  // Create expedition
  const handleCreateExpedition = async () => {
    if (actionLoading) return;
    if (!user) {
      toast.error("Please log in to create an expedition");
      return;
    }
    if (!expeditionForm.boss_id) {
      toast.error("Please select a boss");
      return;
    }
    if (!expeditionForm.expected_time || new Date(expeditionForm.expected_time) <= new Date()) {
      toast.error("Expected time must be in the future");
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/expeditions`, {
        boss_id: expeditionForm.boss_id,
        expected_time: expeditionForm.expected_time,
        expected_runtime: expeditionForm.expected_runtime ? parseInt(expeditionForm.expected_runtime) : null,
        price_for_client: expeditionForm.price_for_client,
        hb_provided: expeditionForm.hb_provided,
        hp_gear_provided: expeditionForm.hp_gear_provided,
        hp_gear_value: expeditionForm.hp_gear_provided ? expeditionForm.hp_gear_value : null,
        safe_run_guaranteed: expeditionForm.safe_run_guaranteed,
        selling_service: expeditionForm.selling_service,
        total_slots: expeditionForm.total_slots,
        free_for_all: expeditionForm.free_for_all,
        job_requirements: expeditionForm.free_for_all ? [] : expeditionForm.job_requirements,
        message: expeditionForm.message || null,
        leader_characters: expeditionForm.leader_characters,
        selected_wish_id: expeditionForm.selected_wish_id || null,
        invite_lfg_ids: expeditionForm.invite_lfg_ids || []
      });
      
      const invitedCount = expeditionForm.invite_lfg_ids?.length || 0;
      let msg = "Expedition created!";
      if (expeditionForm.selected_wish_id) msg += " Buyer notified to confirm.";
      if (invitedCount > 0) msg += ` ${invitedCount} hunter(s) invited.`;
      toast.success(msg);
      
      setShowCreateExpedition(false);
      setExpeditionForm({
        boss_id: "",
        expected_time: "",
        price_for_client: "",
        hb_provided: false,
        total_slots: 6,
        free_for_all: false,
        job_requirements: [],
        message: "",
        leader_characters: [],
        selected_wish_id: null,
        invite_lfg_ids: []
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create expedition");
    } finally {
      setActionLoading(false);
    }
  };
  
  // Add job requirement to expedition form
  const addJobRequirement = () => {
    if (!newJobReq.job) {
      toast.error("Please select a job");
      return;
    }
    setExpeditionForm({
      ...expeditionForm,
      job_requirements: [...expeditionForm.job_requirements, {
        job: newJobReq.job,
        count: newJobReq.count || 1,
        min_level: newJobReq.min_level ? parseInt(newJobReq.min_level) : null
      }]
    });
    setNewJobReq({ job: "", count: 1, min_level: "" });
  };
  
  // Remove job requirement
  const removeJobRequirement = (index) => {
    setExpeditionForm({
      ...expeditionForm,
      job_requirements: expeditionForm.job_requirements.filter((_, i) => i !== index)
    });
  };
  
  // Apply to expedition with selected characters
  const handleApplyExpedition = async (expeditionId) => {
    if (!user) {
      toast.error("Please log in to apply");
      return;
    }
    
    if (applyForm.selected_characters.length === 0) {
      toast.error("Please select at least one character");
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/expeditions/${expeditionId}/apply`, {
        characters: applyForm.selected_characters,
        message: applyForm.message || null
      });
      toast.success("Application submitted!");
      setShowApplyExpedition(null);
      setApplyForm({ selected_characters: [], message: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply");
    }
  };
  
  // Toggle character selection for expedition apply
  const toggleCharacterSelection = (char) => {
    const isSelected = applyForm.selected_characters.some(c => c.character_id === char.id);
    if (isSelected) {
      setApplyForm({
        ...applyForm,
        selected_characters: applyForm.selected_characters.filter(c => c.character_id !== char.id)
      });
    } else {
      setApplyForm({
        ...applyForm,
        selected_characters: [...applyForm.selected_characters, {
          character_id: char.id,
          job: char.job,
          level: char.level,
          name: char.name
        }]
      });
    }
  };
  
  // Apply to expedition as CLIENT
  const handleApplyAsClient = async (expeditionId) => {
    if (!user) { toast.error("Please log in"); return; }
    if (!clientApplyIgn.trim()) { toast.error("Contact IGN is required"); return; }
    try {
      await axios.post(`${API_URL}/api/expeditions/${expeditionId}/apply`, {
        role: "client",
        contact_ign: clientApplyIgn,
        message: `Buyer application from ${user.username}`
      });
      toast.success("Applied as buyer! Wait for the leader to accept.");
      setShowApplyAsClient(null);
      setClientApplyIgn("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply as buyer");
    }
  };

  // Admin delete bounty with reason
  const handleAdminCancelBounty = async (bountyId) => {
    const reason = window.prompt("Enter reason for deleting this bounty:", "Violation of rules");
    if (reason === null) return; // cancelled
    try {
      await axios.delete(`${API_URL}/api/admin/bounties/${bountyId}`, {
        data: { reason: reason || "Violation of rules" }
      });
      toast.success("Bounty deleted. User notified.");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete bounty");
    }
  };
  
  // Admin delete expedition with reason
  const handleAdminCancelExpedition = async (expeditionId) => {
    const reason = window.prompt("Enter reason for deleting this expedition:", "Violation of rules");
    if (reason === null) return;
    try {
      await axios.delete(`${API_URL}/api/admin/expeditions/${expeditionId}`, {
        data: { reason: reason || "Violation of rules" }
      });
      toast.success("Expedition deleted. All participants notified.");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete expedition");
    }
  };
  
  // Create wish to join
  const handleCreateWish = async (selectedCharacters = []) => {
    if (actionLoading) return;
    if (!user) {
      toast.error("Please log in to create a wish");
      return;
    }
    if (!wishForm.contact_ign.trim() && selectedCharacters.length === 0) {
      toast.error("Contact IGN or at least one character is required");
      return;
    }
    
    setActionLoading(true);
    try {
      const payload = { ...wishForm };
      if (selectedCharacters.length > 0) {
        payload.characters = selectedCharacters;
        if (!payload.contact_ign.trim()) {
          payload.contact_ign = selectedCharacters[0].name || selectedCharacters[0].job;
        }
      }
      const authHeaders = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      await axios.post(`${API_URL}/api/expeditions/wish`, payload, authHeaders);
      toast.success("Wish created! Hunters can now find you.");
      setShowCreateWish(false);
      setWishForm({ boss_id: "", reward_offered: "", contact_ign: "", hb_required: false, hp_gear_required: false, safe_run_required: false, message: "", queue_duration_hours: 4 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create wish");
    } finally {
      setActionLoading(false);
    }
  };

  // Accept expedition invite
  const handleAcceptExpeditionInvite = async (expeditionId) => {
    try {
      const invite = pendingExpeditionInvites.find(i => i.expedition_id === expeditionId);
      if (!invite) {
        toast.error("Invite not found");
        return;
      }
      const authHeaders = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      await axios.put(`${API_URL}/api/expeditions/${expeditionId}/accept-invite`, 
        { member_id: invite.id },
        authHeaders
      );
      toast.success("Invite accepted! You've joined the expedition.");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept invite");
    }
  };

  // Create hunter LFG post
  const handleCreateLfg = async () => {
    if (actionLoading) return;
    if (!user) { toast.error("Please log in"); return; }
    if (lfgForm.selected_characters.length === 0) { toast.error("Select at least one character"); return; }
    if (lfgForm.boss_ids.length === 0) { toast.error("Select at least one boss"); return; }
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/expeditions/lfg`, {
        boss_ids: lfgForm.boss_ids,
        characters: lfgForm.selected_characters,
        has_hb: lfgForm.has_hb,
        message: lfgForm.message || null,
        queue_duration_hours: lfgForm.queue_duration_hours
      });
      toast.success("LFG posted! Expedition leaders can find you.");
      setShowCreateLfg(false);
      setLfgForm({ boss_ids: [], selected_characters: [], has_hb: false, message: "", queue_duration_hours: 4 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create LFG");
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle character for LFG form
  const toggleLfgCharacter = (char) => {
    const isSelected = lfgForm.selected_characters.some(c => c.character_id === char.id);
    if (isSelected) {
      setLfgForm({ ...lfgForm, selected_characters: lfgForm.selected_characters.filter(c => c.character_id !== char.id) });
    } else {
      setLfgForm({ ...lfgForm, selected_characters: [...lfgForm.selected_characters, { character_id: char.id, job: char.job, level: char.level, name: char.name }] });
    }
  };

  // Toggle boss for LFG form
  const toggleLfgBoss = (bossId) => {
    if (lfgForm.boss_ids.includes(bossId)) {
      setLfgForm({ ...lfgForm, boss_ids: lfgForm.boss_ids.filter(id => id !== bossId) });
    } else {
      setLfgForm({ ...lfgForm, boss_ids: [...lfgForm.boss_ids, bossId] });
    }
  };
  
  // Member action (expedition leader)
  const handleMemberAction = async (expeditionId, memberId, action) => {
    try {
      await axios.put(`${API_URL}/api/expeditions/${expeditionId}/member-action`, {
        member_id: memberId,
        action: action
      });
      toast.success(`Member ${action}ed`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to perform action");
    }
  };
  
  // Confirm expedition
  const handleConfirmExpedition = async (expeditionId) => {
    try {
      await axios.put(`${API_URL}/api/expeditions/${expeditionId}/confirm`, {
        screenshot: confirmForm.screenshot,
        comment: confirmForm.comment
      });
      toast.success("Confirmation recorded!");
      setShowConfirmDialog(null);
      setConfirmForm({ screenshot: null, comment: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm");
    }
  };
  
  // Filter bounties
  const filteredBounties = bounties.filter(b => {
    if (searchTerm && !b.boss_name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });
  
  // Filter expeditions
  const filteredExpeditions = expeditions.filter(e => {
    // Hide completed expeditions from the browse list
    if (["SUCCEEDED", "SETTLED"].includes(e.status)) return false;
    if (searchTerm && !e.boss_name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    // Boss filter
    if (expBossFilter !== "ALL" && e.boss_name !== expBossFilter) return false;
    // Time filter
    if (expTimeFilter !== "ALL" && e.expected_time) {
      const expDate = new Date(e.expected_time);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
      const dayAfterTomorrow = new Date(today); dayAfterTomorrow.setDate(today.getDate() + 2);
      const endOfWeek = new Date(today); endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
      if (expTimeFilter === "TODAY" && (expDate < today || expDate >= tomorrow)) return false;
      if (expTimeFilter === "TOMORROW" && (expDate < tomorrow || expDate >= dayAfterTomorrow)) return false;
      if (expTimeFilter === "WEEK" && (expDate < today || expDate > endOfWeek)) return false;
    }
    return true;
  });
  const uniqueExpBossNames = [...new Set(expeditions.filter(e => !["SUCCEEDED","SETTLED"].includes(e.status)).map(e => e.boss_name))].sort();
  
  // Buyer-specific filter: hide expeditions that don't allow buyers
  const buyerFilteredExpeditions = filteredExpeditions.filter(e => e.selling_service !== false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(180deg, #1a0a0a 0%, #2d1810 50%, #1a0a0a 100%)" }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg, #1a0a0a 0%, #2d1810 50%, #1a0a0a 100%)" }}>
      {/* Global Notifications */}
      {hcNotificationsEnabled && (
        <FightNotificationContainer
          notifications={fightNotifications}
          onDismiss={dismissFightNotification}
        />
      )}
      
      {/* Header with Login/User Menu */}
      <div 
        className="sticky top-0 z-50"
        style={{ 
          background: "linear-gradient(180deg, rgba(139,69,19,0.3) 0%, rgba(0,0,0,0.8) 100%)",
          borderBottom: "2px solid rgba(255,193,7,0.4)",
          backdropFilter: "blur(8px)"
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                data-testid="hc-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <img 
                src="/hc-logo.png" 
                alt="Hunter's Chance" 
                className="hidden sm:block"
                style={{ 
                  width: "125px",
                  height: "73px",
                  objectFit: "contain",
                  filter: "drop-shadow(0 0 6px rgba(255,215,0,0.4))",
                  borderRadius: "8px"
                }}
              />
              <div>
                <h1 
                  className="text-xl font-black"
                  style={{ 
                    color: "#FFD700",
                    textShadow: "2px 2px 4px rgba(0,0,0,0.8), 0 0 10px rgba(255,215,0,0.3)"
                  }}
                >
                  HUNTER'S CHANCE
                </h1>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* HC Alerts Toggle */}
              {user && (
                <Button
                  variant={hcNotificationsEnabled ? "default" : "outline"}
                  size="sm"
                  onClick={toggleHcNotifications}
                  className="gap-1 hidden sm:flex text-xs"
                  style={hcNotificationsEnabled ? { background: "linear-gradient(135deg, #8B4513 0%, #5D3A1A 100%)", border: "1px solid #FFD700" } : {}}
                  data-testid="hc-alerts-toggle"
                >
                  {hcNotificationsEnabled ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
                  {hcNotificationsEnabled ? "Alerts On" : "Alerts Off"}
                </Button>
              )}
              
              {/* Log Button */}
              {user && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFightLog(true)}
                  className="gap-1 hidden sm:flex text-xs text-amber-400 border-amber-500/30 hover:bg-amber-500/10"
                  data-testid="hc-log-btn"
                >
                  <History className="w-3.5 h-3.5" />
                  Log
                  {fightNotificationHistory.length > 0 && (
                    <span className="bg-amber-500/20 px-1 rounded text-[10px]">{fightNotificationHistory.length}</span>
                  )}
                </Button>
              )}
              
              {/* Reputation badges */}
              {user && (
                <>
                  <div className="hidden md:flex items-center gap-1 px-2 py-1 rounded-lg" style={{ background: "rgba(255,193,7,0.15)", border: "1px solid rgba(255,193,7,0.3)" }}>
                    <Crown className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-amber-300 text-xs font-bold">{user.client_reputation || 0}</span>
                  </div>
                  <div className="hidden md:flex items-center gap-1 px-2 py-1 rounded-lg" style={{ background: "rgba(185,28,28,0.15)", border: "1px solid rgba(185,28,28,0.3)" }}>
                    <Sword className="w-3.5 h-3.5 text-red-400" />
                    <span className="text-red-300 text-xs font-bold">{user.hunter_reputation || 0}</span>
                  </div>
                </>
              )}
              
              {/* Notification Bell */}
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="relative text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" data-testid="hc-bell-btn">
                      <Bell className="w-5 h-5" />
                      {bellNotifications.total > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold">
                          {bellNotifications.total > 9 ? "9+" : bellNotifications.total}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 max-h-[400px] overflow-y-auto">
                    <div className="flex items-center justify-between px-2">
                      <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                      {bellNotifications.hunter_chance_notifications?.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            clearAllBellNotifications();
                          }}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Clear All
                        </Button>
                      )}
                    </div>
                    <DropdownMenuSeparator />
                    {bellNotifications.total === 0 && (
                      <div className="py-4 text-center text-sm text-muted-foreground">No new notifications</div>
                    )}
                    {bellNotifications.hunter_chance_notifications?.map((n) => (
                      <div 
                        key={n.id} 
                        className="group relative flex items-start gap-2 px-2 py-2 hover:bg-accent/50 cursor-pointer rounded-sm"
                        onClick={() => {
                          // For admin deleted notifications, just clear it - don't navigate
                          if (n.type === "admin_bounty_deleted" || n.type === "admin_expedition_deleted") {
                            clearSingleBellNotification(n.id, { stopPropagation: () => {} });
                          } else if (n.bounty_id) {
                            navigate(`/hunters-chance/bounty/${n.bounty_id}`);
                          } else if (n.expedition_id) {
                            navigate(`/hunters-chance/expedition/${n.expedition_id}`);
                          }
                        }}
                      >
                        {n.type?.includes("bounty") ? <Target className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" /> : <Users className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm leading-tight">{getBellNotificationDescription(n)}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{n.boss_name}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-400 hover:bg-red-500/10 flex-shrink-0"
                          onClick={(e) => clearSingleBellNotification(n.id, e)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              {/* User Menu / Login */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-amber-300 hover:bg-amber-500/10 gap-1" data-testid="hc-user-menu">
                      {user.avatar ? (
                        <img src={user.avatar} alt="" className="w-6 h-6 rounded-full" />
                      ) : (
                        <UserCircle className="w-5 h-5" />
                      )}
                      <span className="hidden sm:inline text-xs">{user.username}</span>
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>{user.username}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer">
                      <UserCircle className="w-4 h-4 mr-2" /> Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/")} className="cursor-pointer">
                      <ArrowLeft className="w-4 h-4 mr-2" /> Boss Tracker
                    </DropdownMenuItem>
                    {(user.is_admin || user.is_junior_admin) && (
                      <>
                        <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer text-amber-400">
                          <Shield className="w-4 h-4 mr-2" /> Admin Panel
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/hunters-chance/admin")} className="cursor-pointer text-purple-400">
                          <BarChart3 className="w-4 h-4 mr-2" /> HC Dashboard
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} className="cursor-pointer text-red-400">
                      <LogIn className="w-4 h-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  size="sm"
                  className="text-xs"
                  style={{ background: "linear-gradient(135deg, #8B4513 0%, #5D3A1A 100%)", border: "1px solid #FFD700" }}
                  onClick={() => setShowAuth && setShowAuth(true)}
                  data-testid="hc-login-btn"
                >
                  <LogIn className="w-4 h-4 mr-1" /> Login
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Quick Access: Active Expeditions & Bounties Bar */}
        {user && (() => {
          const preparing = myExpeditions.filter(e => e.status === "ACTIVE");
          const ongoing = myExpeditions.filter(e => e.status === "ONGOING");
          const needsExpConfirm = myExpeditions.filter(e => e.status === "AWAITING_BUYER_CONFIRM" && e.buyer_id === user?.id);
          const settling = myExpeditions.filter(e => 
            (e.status === "SETTLING_SPLITS") || 
            (e.status === "AWAITING_BUYER_CONFIRM" && e.buyer_id !== user?.id)
          );
          const activeBounties = myBounties.filter(b => b.status === "ACTIVE" && b.client_id === user?.id);
          const needsBountyConfirm = myBounties.filter(b => 
            b.status === "ASSIGNED" && (
              (b.client_id === user?.id && !b.client_confirmed && b.hunter_confirmed) ||
              (b.hunter_id === user?.id && !b.hunter_confirmed && b.client_confirmed)
            )
          );
          const assignedBlue = myBounties.filter(b => 
            b.status === "ASSIGNED" && !needsBountyConfirm.some(nc => nc.id === b.id)
          );
          const hasExps = preparing.length > 0 || ongoing.length > 0 || settling.length > 0 || needsExpConfirm.length > 0;
          const hasBounties = activeBounties.length > 0 || needsBountyConfirm.length > 0 || assignedBlue.length > 0;
          const unreadRooms = bellNotifications.rooms_with_unread_chat || [];
          const expChat = (id) => unreadRooms.includes(`expedition_${id}`);
          const btyChat = (id) => unreadRooms.includes(`bounty_${id}`);
          if (!hasExps && !hasBounties) return null;
          return (
            <div className="flex items-center gap-2 flex-wrap mb-4 p-2 rounded-lg bg-black/30 border border-purple-500/20" data-testid="quick-expedition-bar">
              <style>{`@keyframes urgentBlink { 0%, 100% { opacity: 1; box-shadow: 0 0 8px rgba(239,68,68,0.8), inset 0 0 4px rgba(239,68,68,0.3); } 50% { opacity: 0.6; box-shadow: 0 0 20px rgba(239,68,68,1), inset 0 0 10px rgba(239,68,68,0.5); } } @keyframes chatBlink { 0%, 100% { box-shadow: 0 0 6px rgba(234,179,8,0.6); border-color: rgba(234,179,8,0.8); } 50% { box-shadow: 0 0 16px rgba(234,179,8,1); border-color: rgba(234,179,8,1); } } @keyframes huntingBlink { 0%, 100% { box-shadow: 0 0 6px rgba(59,130,246,0.6); border-color: rgba(59,130,246,0.8); } 50% { box-shadow: 0 0 16px rgba(59,130,246,1); border-color: rgba(59,130,246,1); } }`}</style>
              {/* Expeditions */}
              {hasExps && (
                <>
                  <span className="text-[10px] text-purple-400/60 uppercase tracking-wider mr-1">Expeditions:</span>
                  {/* Needs buyer confirmation - URGENT */}
                  {needsExpConfirm.map(exp => (
                    <Button
                      key={exp.id}
                      size="sm"
                      className="h-8 px-2 bg-red-700/40 hover:bg-red-600/60 text-red-100 border-2 border-red-500 text-xs gap-1"
                      style={{ animation: "urgentBlink 0.8s ease-in-out infinite" }}
                      onClick={() => navigate(`/hunters-chance/expedition/${exp.id}`)}
                      data-testid={`quick-exp-confirm-${exp.id}`}
                    >
                      <AlertTriangle className="w-4 h-4 text-red-300" />
                      <img src={getBossImage(exp.boss_name)} alt="" className="w-5 h-5 rounded" />
                      <span className="max-w-[80px] truncate font-bold">{exp.boss_name}</span>
                      <Badge className="bg-red-500/50 text-white text-[8px] px-1 py-0 font-bold">CONFIRM!</Badge>
                    </Button>
                  ))}
                  {preparing.map(exp => (
                    <Button
                      key={exp.id}
                      size="sm"
                      className={`h-8 px-2 text-xs gap-1 ${expChat(exp.id) ? 'bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-200 border-2 border-yellow-500' : 'bg-blue-600/20 hover:bg-blue-600/40 text-blue-200 border border-blue-500/30'}`}
                      style={expChat(exp.id) ? { animation: "chatBlink 1.2s ease-in-out infinite" } : {}}
                      onClick={() => navigate(`/hunters-chance/expedition/${exp.id}`)}
                      data-testid={`quick-exp-${exp.id}`}
                    >
                      {expChat(exp.id) && <MessageSquare className="w-3.5 h-3.5 text-yellow-300" />}
                      <img src={getBossImage(exp.boss_name)} alt="" className="w-5 h-5 rounded" />
                      <span className="max-w-[80px] truncate">{exp.boss_name}</span>
                      <Badge className="bg-blue-500/30 text-blue-200 text-[8px] px-1 py-0">Prep</Badge>
                    </Button>
                  ))}
                  {ongoing.map(exp => (
                    <Button
                      key={exp.id}
                      size="sm"
                      className={`h-8 px-2 text-xs gap-1 ${expChat(exp.id) ? 'bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-200 border-2 border-yellow-500' : 'bg-green-600/20 hover:bg-green-600/40 text-green-200 border border-green-500/30'}`}
                      style={expChat(exp.id) ? { animation: "chatBlink 1.2s ease-in-out infinite" } : {}}
                      onClick={() => navigate(`/hunters-chance/expedition/${exp.id}`)}
                      data-testid={`quick-exp-${exp.id}`}
                    >
                      {expChat(exp.id) && <MessageSquare className="w-3.5 h-3.5 text-yellow-300" />}
                      <img src={getBossImage(exp.boss_name)} alt="" className="w-5 h-5 rounded" />
                      <span className="max-w-[80px] truncate">{exp.boss_name}</span>
                      <Badge className="bg-green-500/30 text-green-200 text-[8px] px-1 py-0">Live</Badge>
                    </Button>
                  ))}
                  {settling.length > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" className="h-8 px-2 bg-amber-600/20 hover:bg-amber-600/40 text-amber-200 border border-amber-500/30 text-xs gap-1">
                          <Coins className="w-4 h-4" />
                          Settling ({settling.length})
                          <ChevronDown className="w-3 h-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {settling.map(exp => (
                          <DropdownMenuItem key={exp.id} onClick={() => navigate(`/hunters-chance/expedition/${exp.id}`)} className="cursor-pointer gap-2">
                            <img src={getBossImage(exp.boss_name)} alt="" className="w-5 h-5 rounded" />
                            <span>{exp.boss_name}</span>
                            <Badge className="text-[8px]">{exp.status === "AWAITING_BUYER_CONFIRM" ? "Awaiting" : "Splits"}</Badge>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </>
              )}
              {/* Divider */}
              {hasExps && hasBounties && <div className="w-px h-5 bg-white/10 mx-1" />}
              {/* Bounties */}
              {hasBounties && (
                <>
                  <span className="text-[10px] text-rose-400/60 uppercase tracking-wider mr-1">Bounties:</span>
                  {/* Needs confirmation - URGENT */}
                  {needsBountyConfirm.map(b => (
                    <Button
                      key={b.id}
                      size="sm"
                      className="h-8 px-2 bg-red-700/40 hover:bg-red-600/60 text-red-100 border-2 border-red-500 text-xs gap-1"
                      style={{ animation: "urgentBlink 0.8s ease-in-out infinite" }}
                      onClick={() => navigate(`/hunters-chance/bounty/${b.id}`)}
                      data-testid={`quick-bounty-confirm-${b.id}`}
                    >
                      <AlertTriangle className="w-4 h-4 text-red-300" />
                      <img src={getBossImage(b.boss_name)} alt="" className="w-5 h-5 rounded" />
                      <span className="max-w-[80px] truncate font-bold">{b.boss_name}</span>
                      <Badge className="bg-red-500/50 text-white text-[8px] px-1 py-0 font-bold">CONFIRM!</Badge>
                    </Button>
                  ))}
                  {activeBounties.map(b => (
                    <Button
                      key={b.id}
                      size="sm"
                      className={`h-8 px-2 text-xs gap-1 ${btyChat(b.id) ? 'bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-200 border-2 border-yellow-500' : 'bg-rose-600/20 hover:bg-rose-600/40 text-rose-200 border border-rose-500/30'}`}
                      style={btyChat(b.id) ? { animation: "chatBlink 1.2s ease-in-out infinite" } : {}}
                      onClick={() => navigate(`/hunters-chance/bounty/${b.id}`)}
                      data-testid={`quick-bounty-${b.id}`}
                    >
                      {btyChat(b.id) && <MessageSquare className="w-3.5 h-3.5 text-yellow-300" />}
                      <img src={getBossImage(b.boss_name)} alt="" className="w-5 h-5 rounded" />
                      <span className="max-w-[80px] truncate">{b.boss_name}</span>
                      <Badge className="bg-rose-500/30 text-rose-200 text-[8px] px-1 py-0">Posted</Badge>
                    </Button>
                  ))}
                  {assignedBlue.map(b => {
                    const hasChat = btyChat(b.id);
                    const isHunting = b.hunter_id === user?.id;
                    return (
                      <Button
                        key={b.id}
                        size="sm"
                        className={`h-8 px-2 text-xs gap-1 ${hasChat ? 'bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-200 border-2 border-yellow-500' : 'bg-blue-600/20 hover:bg-blue-600/40 text-blue-200 border-2 border-blue-500'}`}
                        style={hasChat ? { animation: "chatBlink 1.2s ease-in-out infinite" } : { animation: "huntingBlink 1.5s ease-in-out infinite" }}
                        onClick={() => navigate(`/hunters-chance/bounty/${b.id}`)}
                        data-testid={`quick-bounty-${b.id}`}
                      >
                        {hasChat ? <MessageSquare className="w-3.5 h-3.5 text-yellow-300" /> : <Swords className="w-3.5 h-3.5 text-blue-300" />}
                        <img src={getBossImage(b.boss_name)} alt="" className="w-5 h-5 rounded" />
                        <span className="max-w-[80px] truncate">{b.boss_name}</span>
                        <Badge className="bg-blue-500/30 text-blue-200 text-[8px] px-1 py-0">
                          {isHunting ? "Hunting" : "Accepted"}
                        </Badge>
                      </Button>
                    );
                  })}
                </>
              )}
            </div>
          );
        })()}

        {/* Main Tabs: Buyer / Hunter / Rankings */}
        <Tabs value={mainTab} onValueChange={handleMainTabChange} className="mb-6">
          <TabsList 
            className="grid w-full max-w-md mx-auto grid-cols-3"
            style={{ 
              background: "linear-gradient(180deg, rgba(139,69,19,0.3) 0%, rgba(0,0,0,0.5) 100%)",
              border: "1px solid rgba(255,193,7,0.3)"
            }}
          >
            <TabsTrigger 
              value="client" 
              className="data-[state=active]:bg-amber-500/30 data-[state=active]:text-amber-300 text-amber-500/70"
            >
              <Crown className="w-4 h-4 mr-2" />
              Buyer
            </TabsTrigger>
            <TabsTrigger 
              value="hunter" 
              className="data-[state=active]:bg-red-500/30 data-[state=active]:text-red-300 text-red-500/70"
            >
              <Sword className="w-4 h-4 mr-2" />
              Hunter
            </TabsTrigger>
            <TabsTrigger 
              value="rankings" 
              className="data-[state=active]:bg-amber-500/30 data-[state=active]:text-amber-300 text-amber-500/70"
            >
              <Trophy className="w-4 h-4 mr-2" />
              Rankings
            </TabsTrigger>
          </TabsList>
          
          {/* BUYER TAB */}
          <TabsContent value="client" className="mt-6">
            <Tabs value={subTab} onValueChange={handleSubTabChange}>
              <TabsList className="mb-6 bg-black/40">
                <TabsTrigger value="bounty" className="data-[state=active]:bg-amber-500/30">
                  <Target className="w-4 h-4 mr-2" />
                  Bounty
                </TabsTrigger>
                <TabsTrigger value="expedition" className="data-[state=active]:bg-blue-500/30">
                  <Users className="w-4 h-4 mr-2" />
                  Expedition
                </TabsTrigger>
                <TabsTrigger value="my-bounties" className="data-[state=active]:bg-purple-500/30">
                  <Archive className="w-4 h-4 mr-2" />
                  My Bounties
                </TabsTrigger>
                <TabsTrigger value="my-expeditions" className="data-[state=active]:bg-green-500/30">
                  <Users className="w-4 h-4 mr-2" />
                  My Expeditions
                </TabsTrigger>
              </TabsList>
              
              {/* Buyer: Create Bounty */}
              <TabsContent value="bounty">
                <div className="space-y-6">
                  {/* Create Bounty Button */}
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-white">Post a Bounty</h2>
                    <Button
                      onClick={() => user ? setShowCreateBounty(true) : toast.error("Please log in")}
                      className="bg-amber-500 hover:bg-amber-600 text-black"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Bounty
                    </Button>
                  </div>
                  
                  <Card className="bg-black/40 border-amber-500/30">
                    <CardHeader>
                      <CardTitle className="text-amber-400">How Bounties Work</CardTitle>
                    </CardHeader>
                    <CardContent className="text-amber-200/70 space-y-2">
                      <p>1. Select a boss you want killed and set your reward</p>
                      <p>2. Provide up to 3 IGN contacts for hunters to reach you</p>
                      <p>3. Set the duration (30 min - 12 hours)</p>
                      <p>4. A hunter will accept and contact you in-game</p>
                      <p>5. Both parties confirm completion to earn reputation</p>
                      <p className="text-amber-400 font-semibold mt-4">⚠️ Be available during the bounty duration!</p>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              {/* Buyer: Search Expeditions */}
              <TabsContent value="expedition">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-white">Join an Expedition</h2>
                    <Button
                      onClick={() => user ? setShowCreateWish(true) : toast.error("Please log in")}
                      variant="outline"
                      className="border-blue-500/50 text-blue-400"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Wish to Join
                    </Button>
                  </div>
                  
                  {/* Search & Filters */}
                  <div className="flex gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[180px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search expeditions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-black/40 border-gray-600"
                      />
                    </div>
                    <select
                      value={expTimeFilter}
                      onChange={(e) => setExpTimeFilter(e.target.value)}
                      className="bg-black/40 border border-gray-600 rounded-md px-3 py-2 text-sm text-gray-200"
                      data-testid="exp-time-filter"
                    >
                      <option value="ALL">All Times</option>
                      <option value="TODAY">Today</option>
                      <option value="TOMORROW">Tomorrow</option>
                      <option value="WEEK">This Week</option>
                    </select>
                    <select
                      value={expBossFilter}
                      onChange={(e) => setExpBossFilter(e.target.value)}
                      className="bg-black/40 border border-gray-600 rounded-md px-3 py-2 text-sm text-gray-200"
                      data-testid="exp-boss-filter"
                    >
                      <option value="ALL">All Bosses</option>
                      {uniqueExpBossNames.map(name => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Expedition List */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {buyerFilteredExpeditions.map((exp) => (
                      <ExpeditionPoster
                        key={exp.id}
                        expedition={exp}
                        isLeader={user && exp.leader_id === user.id}
                        isAdmin={user && (user.is_admin || user.is_junior_admin)}
                        hasPendingInvite={pendingExpeditionInvites.some(i => i.expedition_id === exp.id)}
                        isMember={myExpeditions.some(e => e.id === exp.id)}
                        onApply={() => setShowApplyExpedition(exp)}
                        onApplyAsClient={() => setShowApplyAsClient(exp)}
                        onAcceptInvite={() => handleAcceptExpeditionInvite(exp.id)}
                        bossImage={getBossImage(exp.boss_name)}
                        onLeave={async () => {
                          if (!window.confirm("Leave this expedition?")) return;
                          try {
                            await axios.delete(`${API_URL}/api/expeditions/${exp.id}/leave`);
                            toast.success("Left the expedition");
                            loadData();
                          } catch (err) {
                            toast.error("Failed to leave expedition");
                          }
                        }}
                        onViewDetails={() => navigate(`/hunters-chance/expedition/${exp.id}`)}
                        onAdminCancel={() => handleAdminCancelExpedition(exp.id)}
                        onViewUser={(uid) => setShowUserProfile(uid)}
                        parchmentBg={PARCHMENT_BG}
                      />
                    ))}
                    
                    {buyerFilteredExpeditions.length === 0 && (
                      <div className="col-span-full text-center py-12 text-gray-400">
                        <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No expeditions found</p>
                        <p className="text-sm">Create a "Wish to Join" to let hunters know you're interested!</p>
                      </div>
                    )}
                  </div>

                </div>
              </TabsContent>
              
              {/* Buyer: My Bounties */}
              <TabsContent value="my-bounties">
                <div className="space-y-6">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h2 className="text-xl font-semibold text-white">My Bounties</h2>
                    <select
                      value={myBountyStatusFilter}
                      onChange={(e) => setMyBountyStatusFilter(e.target.value)}
                      className="bg-black/40 border border-amber-500/30 text-amber-200 text-xs rounded px-2 py-1"
                      data-testid="my-bounty-status-filter"
                    >
                      <option value="ALL">All Statuses</option>
                      <option value="ACTIVE">Active</option>
                      <option value="ACCEPTED">Accepted</option>
                      <option value="SUCCEEDED">Succeeded</option>
                      <option value="CANCELLED">Cancelled</option>
                      <option value="EXPIRED">Expired</option>
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {myBounties.filter(b => b.client_id === user?.id && (myBountyStatusFilter === "ALL" || b.status === myBountyStatusFilter)).map((bounty) => (
                      <BountyPoster
                        key={bounty.id}
                        bounty={bounty}
                        isOwner={true}
                        onCancel={() => handleCancelBounty(bounty.id)}
                        onRestore={() => handleRestoreBounty(bounty.id)}
                        onConfirm={() => setShowConfirmDialog({ type: "bounty", id: bounty.id })}
                        onViewDetails={() => navigate(`/hunters-chance/bounty/${bounty.id}`)}
                        onViewUser={(uid) => setShowUserProfile(uid)}
                        onViewClient={() => setShowUserProfile(bounty.client_id)}
                        parchmentBg={PARCHMENT_BG}
                        bossImage={getBossImage(bounty.boss_name)}
                      />
                    ))}
                    
                    {myBounties.filter(b => b.client_id === user?.id && (myBountyStatusFilter === "ALL" || b.status === myBountyStatusFilter)).length === 0 && (
                      <div className="col-span-full text-center py-12 text-gray-400">
                        <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>You haven't created any bounties yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Buyer: My Expeditions */}
              <TabsContent value="my-expeditions">
                <div className="space-y-6">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h2 className="text-xl font-semibold text-white">My Expeditions (as Buyer)</h2>
                    <select
                      value={myExpStatusFilter}
                      onChange={(e) => setMyExpStatusFilter(e.target.value)}
                      className="bg-black/40 border border-green-500/30 text-green-200 text-xs rounded px-2 py-1"
                      data-testid="my-exp-status-filter"
                    >
                      <option value="ALL">All Statuses</option>
                      <option value="ACTIVE">Active</option>
                      <option value="ONGOING">Ongoing</option>
                      <option value="AWAITING_BUYER_CONFIRM">Awaiting Confirm</option>
                      <option value="SETTLING_SPLITS">Settling Splits</option>
                      <option value="SUCCEEDED">Succeeded</option>
                      <option value="ARCHIVED">Archived</option>
                    </select>
                  </div>
                  
                  {/* Expeditions awaiting confirmation - highlighted */}
                  {(myExpStatusFilter === "ALL" || myExpStatusFilter === "AWAITING_BUYER_CONFIRM") && myExpeditions.filter(e => e.client_id === user?.id && e.status === "AWAITING_BUYER_CONFIRM").length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-green-400 uppercase tracking-wider flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" /> Awaiting Your Confirmation
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {myExpeditions.filter(e => e.client_id === user?.id && e.status === "AWAITING_BUYER_CONFIRM").map((exp) => (
                          <ExpeditionPoster
                            key={exp.id}
                            expedition={exp}
                            onViewDetails={() => navigate(`/hunters-chance/expedition/${exp.id}`)}
                            onViewUser={(uid) => setShowUserProfile(uid)}
                            bossImage={getBossImage(exp.boss_name)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* All other buyer expeditions */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {myExpeditions.filter(e => e.client_id === user?.id && e.status !== "AWAITING_BUYER_CONFIRM" && (myExpStatusFilter === "ALL" || e.status === myExpStatusFilter)).map((exp) => (
                      <ExpeditionPoster
                        key={exp.id}
                        expedition={exp}
                        onViewDetails={() => navigate(`/hunters-chance/expedition/${exp.id}`)}
                        onViewUser={(uid) => setShowUserProfile(uid)}
                        bossImage={getBossImage(exp.boss_name)}
                      />
                    ))}
                  </div>

                  {myExpeditions.filter(e => e.client_id === user?.id && (myExpStatusFilter === "ALL" || e.status === myExpStatusFilter)).length === 0 && (
                    <div className="text-center py-12 text-gray-400">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>You haven't joined any expeditions as buyer yet</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </TabsContent>
          
          {/* HUNTER TAB */}
          <TabsContent value="hunter" className="mt-6">
            <Tabs value={subTab} onValueChange={handleSubTabChange}>
              <TabsList className="mb-6 bg-black/40">
                <TabsTrigger value="bounty" className="data-[state=active]:bg-amber-500/30">
                  <Target className="w-4 h-4 mr-2" />
                  Find Bounties
                </TabsTrigger>
                <TabsTrigger value="expedition" className="data-[state=active]:bg-blue-500/30">
                  <Users className="w-4 h-4 mr-2" />
                  Expeditions
                </TabsTrigger>
                <TabsTrigger value="my-work" className="data-[state=active]:bg-purple-500/30">
                  <Archive className="w-4 h-4 mr-2" />
                  My Work
                </TabsTrigger>
              </TabsList>
              
              {/* Hunter: Find Bounties */}
              <TabsContent value="bounty">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-white">Active Bounties</h2>
                    <Button onClick={loadData} variant="ghost" size="icon">
                      <RefreshCw className="w-4 h-4 text-gray-400" />
                    </Button>
                  </div>
                  
                  {/* Search */}
                  <div className="flex gap-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search bounties by boss name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-black/40 border-gray-600"
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-40 bg-black/40 border-gray-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                        <SelectItem value="ASSIGNED">Assigned</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Bounty Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredBounties.map((bounty) => (
                      <BountyPoster
                        key={bounty.id}
                        bounty={bounty}
                        isHunter={user && bounty.client_id !== user.id}
                        isAdmin={user && (user.is_admin || user.is_junior_admin)}
                        onAccept={() => handleAcceptBounty(bounty.id)}
                        onViewDetails={() => navigate(`/hunters-chance/bounty/${bounty.id}`)}
                        onViewClient={() => setShowUserProfile(bounty.client_id)}
                        onViewUser={(uid) => setShowUserProfile(uid)}
                        onAdminCancel={() => handleAdminCancelBounty(bounty.id)}
                        parchmentBg={PARCHMENT_BG}
                        bossImage={getBossImage(bounty.boss_name)}
                      />
                    ))}
                    
                    {filteredBounties.length === 0 && (
                      <div className="col-span-full text-center py-12 text-gray-400">
                        <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No bounties found</p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
              
              {/* Hunter: Expeditions */}
              <TabsContent value="expedition">
                <div className="space-y-6">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h2 className="text-xl font-semibold text-white">Expeditions</h2>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => user ? setShowCreateLfg(true) : toast.error("Please log in")}
                        className="bg-purple-500 hover:bg-purple-600 text-white"
                        data-testid="create-lfg-btn"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Post LFG
                      </Button>
                      <Button
                        onClick={() => user ? setShowCreateExpedition(true) : toast.error("Please log in")}
                        className="bg-green-500 hover:bg-green-600 text-black"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Expedition
                      </Button>
                    </div>
                  </div>

                  {/* Search & Filters for Hunter Expeditions */}
                  <div className="flex gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[180px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search expeditions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-black/40 border-gray-600"
                      />
                    </div>
                    <select
                      value={expTimeFilter}
                      onChange={(e) => setExpTimeFilter(e.target.value)}
                      className="bg-black/40 border border-gray-600 rounded-md px-3 py-2 text-sm text-gray-200"
                      data-testid="hunter-exp-time-filter"
                    >
                      <option value="ALL">All Times</option>
                      <option value="TODAY">Today</option>
                      <option value="TOMORROW">Tomorrow</option>
                      <option value="WEEK">This Week</option>
                    </select>
                    <select
                      value={expBossFilter}
                      onChange={(e) => setExpBossFilter(e.target.value)}
                      className="bg-black/40 border border-gray-600 rounded-md px-3 py-2 text-sm text-gray-200"
                      data-testid="hunter-exp-boss-filter"
                    >
                      <option value="ALL">All Bosses</option>
                      {uniqueExpBossNames.map(name => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Expedition Wishes (Buyers looking) */}
                  {expeditionWishes.length > 0 && (
                    <Card className="bg-amber-500/10 border-amber-500/30">
                      <CardHeader>
                        <CardTitle className="text-amber-400 flex items-center gap-2">
                          <Crown className="w-5 h-5" />
                          Buyers Looking for Expeditions
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {expeditionWishes.slice(0, 6).map((wish) => {
                            const isOwner = user && wish.user_id === user.id;
                            return (
                              <div key={wish.id} className="p-4 bg-black/40 rounded-xl border border-amber-500/30 hover:border-amber-500/50 transition-all">
                                {/* User Header */}
                                <div className="flex items-center gap-3 mb-3">
                                  {wish.user_avatar ? (
                                    <img 
                                      src={wish.user_avatar} 
                                      alt={wish.username} 
                                      className="w-12 h-12 rounded-lg border-2 border-amber-500/40 object-cover"
                                      onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }}
                                    />
                                  ) : null}
                                  <div className={`w-12 h-12 rounded-lg border-2 border-amber-500/40 bg-amber-900/30 items-center justify-center text-amber-400 font-bold text-lg ${wish.user_avatar ? 'hidden' : 'flex'}`}>
                                    {wish.username?.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-white font-bold text-lg cursor-pointer hover:text-amber-200 hover:underline" onClick={() => setShowUserProfile(wish.user_id)}>{wish.username}</p>
                                    <div className="flex items-center gap-2">
                                      {wish.guild_name && <span className="text-xs text-amber-300">&lt;{wish.guild_name}&gt;</span>}
                                      {wish.client_reputation > 0 && <span className="text-xs text-yellow-400">⭐ {wish.client_reputation} rep</span>}
                                    </div>
                                  </div>
                                </div>

                                {/* Boss */}
                                <div className="mb-3">
                                  <Badge className="bg-amber-600/30 text-amber-200">{wish.boss_name}</Badge>
                                </div>

                                {/* Offering & Requirements */}
                                <div className="space-y-1 mb-3">
                                  <p className="text-sm text-green-400 font-medium">Offering: {formatMesos(wish.reward_offered)}</p>
                                  {wish.hb_required && <Badge variant="outline" className="text-xs border-red-500/50 text-red-400">HB Required</Badge>}
                                </div>

                                {/* Message */}
                                {wish.message && (
                                  <p className="text-sm text-gray-400 italic mb-3">"{wish.message}"</p>
                                )}

                                {/* Owner controls */}
                                {isOwner && (
                                  <div className="flex gap-2 pt-2 border-t border-amber-500/20">
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="flex-1 text-xs text-amber-300 border-amber-500/30 hover:bg-amber-500/10"
                                      onClick={async () => {
                                        try {
                                          await axios.put(`${API_URL}/api/expeditions/wish/${wish.id}/extend`);
                                          toast.success("Wish extended by 4 hours!");
                                          loadData();
                                        } catch (err) {
                                          toast.error("Failed to extend wish");
                                        }
                                      }}
                                    >
                                      <Clock className="w-3 h-3 mr-1" /> Extend
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="flex-1 text-xs text-red-300 border-red-500/30 hover:bg-red-500/10"
                                      onClick={async () => {
                                        if (!window.confirm("Delete this wish?")) return;
                                        try {
                                          await axios.delete(`${API_URL}/api/expeditions/wish/${wish.id}`);
                                          toast.success("Wish deleted");
                                          loadData();
                                        } catch (err) {
                                          toast.error("Failed to delete wish");
                                        }
                                      }}
                                    >
                                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                                    </Button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Hunter LFG Posts */}
                  {hunterLfgPosts.length > 0 && (
                    <Card className="bg-purple-500/10 border-purple-500/30">
                      <CardHeader>
                        <CardTitle className="text-purple-400 flex items-center gap-2">
                          <Users className="w-5 h-5" />
                          Hunters Looking for Group
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {hunterLfgPosts.slice(0, 8).map((lfg) => {
                            const isOwner = user && lfg.user_id === user.id;
                            return (
                              <div key={lfg.id} className="p-4 bg-black/40 rounded-xl border border-purple-500/30 hover:border-purple-500/50 transition-all" data-testid={`lfg-post-${lfg.id}`}>
                                {/* User Header */}
                                <div className="flex items-center gap-3 mb-3">
                                  {lfg.user_avatar ? (
                                    <img 
                                      src={lfg.user_avatar} 
                                      alt={lfg.username} 
                                      className="w-12 h-12 rounded-lg border-2 border-purple-500/40 object-cover"
                                      onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }}
                                    />
                                  ) : null}
                                  <div className={`w-12 h-12 rounded-lg border-2 border-purple-500/40 bg-purple-900/30 items-center justify-center text-purple-400 font-bold text-lg ${lfg.user_avatar ? 'hidden' : 'flex'}`}>
                                    {lfg.username?.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-white font-bold text-lg cursor-pointer hover:text-purple-200 hover:underline" onClick={() => setShowUserProfile(lfg.user_id)}>{lfg.username}</p>
                                    <div className="flex items-center gap-2">
                                      {lfg.guild_name && <span className="text-xs text-purple-300">&lt;{lfg.guild_name}&gt;</span>}
                                      {lfg.hunter_reputation > 0 && <span className="text-xs text-yellow-400">⭐ {lfg.hunter_reputation} rep</span>}
                                      {lfg.has_hb && <Badge variant="outline" className="text-[10px] py-0 px-1 border-green-500/50 text-green-400">HB</Badge>}
                                    </div>
                                  </div>
                                </div>

                                {/* Bosses looking for */}
                                <div className="mb-3">
                                  <p className="text-xs text-purple-300/60 mb-1">Looking for:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {(lfg.bosses || [{ name: lfg.boss_name }]).slice(0, 3).map((boss, bi) => (
                                      <Badge key={bi} className="bg-purple-600/30 text-purple-200 text-xs">{boss.name || boss}</Badge>
                                    ))}
                                  </div>
                                </div>

                                {/* Characters */}
                                <div className="mb-3">
                                  <p className="text-xs text-purple-300/60 mb-1">Characters:</p>
                                  <div className="space-y-1">
                                    {(lfg.characters || []).map((char, ci) => {
                                      const charSkills = char.skills || availableJobs[char.job]?.skills || [];
                                      return (
                                        <div key={ci} className="flex items-center gap-2 p-1.5 bg-black/30 rounded">
                                          {charSkills.slice(0, 1).map((s, si) => (
                                            <img key={si} src={s.icon} alt={s.name} className="w-6 h-6" />
                                          ))}
                                          <div>
                                            <span className="text-white font-medium text-sm">{char.job}</span>
                                            {char.name && <span className="text-purple-300/70 text-sm ml-1">({char.name})</span>}
                                            <span className="text-purple-300/60 text-xs ml-1">Lv.{char.level}</span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Message */}
                                {lfg.message && (
                                  <p className="text-sm text-gray-400 italic mb-3">"{lfg.message}"</p>
                                )}

                                {/* Owner controls */}
                                {isOwner && (
                                  <div className="flex gap-2 pt-2 border-t border-purple-500/20">
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="flex-1 text-xs text-purple-300 border-purple-500/30 hover:bg-purple-500/10"
                                      onClick={async () => {
                                        try {
                                          await axios.put(`${API_URL}/api/expeditions/lfg/${lfg.id}/extend`);
                                          toast.success("LFG extended by 4 hours!");
                                          loadData();
                                        } catch (err) {
                                          toast.error("Failed to extend LFG");
                                        }
                                      }}
                                    >
                                      <Clock className="w-3 h-3 mr-1" /> Extend
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="flex-1 text-xs text-red-300 border-red-500/30 hover:bg-red-500/10"
                                      onClick={async () => {
                                        if (!window.confirm("Delete this LFG post?")) return;
                                        try {
                                          await axios.delete(`${API_URL}/api/expeditions/lfg/${lfg.id}`);
                                          toast.success("LFG deleted");
                                          loadData();
                                        } catch (err) {
                                          toast.error("Failed to delete LFG");
                                        }
                                      }}
                                    >
                                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                                    </Button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Expedition List */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredExpeditions.map((exp) => (
                      <ExpeditionPoster
                        key={exp.id}
                        expedition={exp}
                        isHunter={true}
                        isLeader={user && exp.leader_id === user.id}
                        isAdmin={user && (user.is_admin || user.is_junior_admin)}
                        hasPendingInvite={pendingExpeditionInvites.some(i => i.expedition_id === exp.id)}
                        isMember={myExpeditions.some(e => e.id === exp.id)}
                        onApply={() => setShowApplyExpedition(exp)}
                        onApplyAsClient={() => setShowApplyAsClient(exp)}
                        onAcceptInvite={() => handleAcceptExpeditionInvite(exp.id)}
                        bossImage={getBossImage(exp.boss_name)}
                        onLeave={async () => {
                          if (!window.confirm("Leave this expedition?")) return;
                          try {
                            await axios.delete(`${API_URL}/api/expeditions/${exp.id}/leave`);
                            toast.success("Left the expedition");
                            loadData();
                          } catch (err) {
                            toast.error("Failed to leave expedition");
                          }
                        }}
                        onViewDetails={() => navigate(`/hunters-chance/expedition/${exp.id}`)}
                        onAdminCancel={() => handleAdminCancelExpedition(exp.id)}
                        onViewUser={(uid) => setShowUserProfile(uid)}
                        parchmentBg={PARCHMENT_BG}
                      />
                    ))}
                  </div>
                </div>
              </TabsContent>
              
              {/* Hunter: My Work */}
              <TabsContent value="my-work">
                <div className="space-y-6">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h2 className="text-xl font-semibold text-white">My Accepted Bounties & Expeditions</h2>
                    <select
                      value={myWorkStatusFilter}
                      onChange={(e) => setMyWorkStatusFilter(e.target.value)}
                      className="bg-black/40 border border-purple-500/30 text-purple-200 text-xs rounded px-2 py-1"
                      data-testid="my-work-status-filter"
                    >
                      <option value="ALL">All Statuses</option>
                      <option value="ACTIVE">Active</option>
                      <option value="ONGOING">Ongoing</option>
                      <option value="AWAITING_BUYER_CONFIRM">Awaiting Buyer</option>
                      <option value="SETTLING_SPLITS">Settling Splits</option>
                      <option value="SUCCEEDED">Succeeded</option>
                      <option value="CANCELLED">Cancelled</option>
                      <option value="ARCHIVED">Archived</option>
                    </select>
                  </div>
                  
                  {/* My Accepted Bounties */}
                  <div>
                    <h3 className="text-lg font-medium text-amber-400 mb-4">Accepted Bounties</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {myBounties.filter(b => b.hunter_id === user?.id && (myWorkStatusFilter === "ALL" || b.status === myWorkStatusFilter)).map((bounty) => (
                        <BountyPoster
                          key={bounty.id}
                          bounty={bounty}
                          isAcceptedHunter={true}
                          onCancel={() => handleCancelBounty(bounty.id)}
                          onConfirm={() => setShowConfirmDialog({ type: "bounty", id: bounty.id })}
                          onReport={(type) => handleReportBounty(bounty.id, type)}
                          onViewDetails={() => navigate(`/hunters-chance/bounty/${bounty.id}`)}
                          onViewUser={(uid) => setShowUserProfile(uid)}
                          onViewClient={() => setShowUserProfile(bounty.client_id)}
                          parchmentBg={PARCHMENT_BG}
                          bossImage={getBossImage(bounty.boss_name)}
                        />
                      ))}
                      
                      {myBounties.filter(b => b.hunter_id === user?.id && (myWorkStatusFilter === "ALL" || b.status === myWorkStatusFilter)).length === 0 && (
                        <div className="col-span-full text-center py-8 text-gray-400">
                          <p>No accepted bounties</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* My Expeditions */}
                  <div>
                    <h3 className="text-lg font-medium text-green-400 mb-4">My Expeditions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {myExpeditions.filter(e => myWorkStatusFilter === "ALL" || e.status === myWorkStatusFilter).map((exp) => (
                        <ExpeditionPoster
                          key={exp.id}
                          expedition={exp}
                          isLeader={exp.leader_id === user?.id}
                          onViewDetails={() => navigate(`/hunters-chance/expedition/${exp.id}`)}
                          onConfirm={() => setShowConfirmDialog({ type: "expedition", id: exp.id })}
                          onViewUser={(uid) => setShowUserProfile(uid)}
                          bossImage={getBossImage(exp.boss_name)}
                          parchmentBg={PARCHMENT_BG}
                        />
                      ))}
                      
                      {myExpeditions.filter(e => myWorkStatusFilter === "ALL" || e.status === myWorkStatusFilter).length === 0 && (
                        <div className="col-span-full text-center py-8 text-gray-400">
                          <p>No expeditions</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </TabsContent>
          
          {/* RANKINGS TAB */}
          <TabsContent value="rankings" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Hunter Rankings */}
              <Card className="bg-black/40 border-green-500/30">
                <CardHeader>
                  <CardTitle className="text-green-400 flex items-center gap-2">
                    <Sword className="w-5 h-5" />
                    Top Hunters
                  </CardTitle>
                  <CardDescription className="text-green-200/60">
                    Reputation earned by completing bounties and expeditions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-96">
                    <div className="space-y-2">
                      {hunterRankings.map((ranking) => (
                        <div
                          key={ranking.user_id}
                          className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg cursor-pointer hover:bg-green-500/20"
                          onClick={() => setShowUserProfile(ranking.user_id)}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                              ranking.rank === 1 ? 'bg-amber-500 text-black' :
                              ranking.rank === 2 ? 'bg-gray-400 text-black' :
                              ranking.rank === 3 ? 'bg-amber-700 text-white' :
                              'bg-gray-600 text-white'
                            }`}>
                              {ranking.rank}
                            </div>
                            {ranking.avatar ? (
                              <img src={ranking.avatar} alt="" className="w-10 h-10 rounded-full" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-green-500/30 flex items-center justify-center">
                                <Sword className="w-5 h-5 text-green-400" />
                              </div>
                            )}
                            <span className="text-white font-medium">{ranking.username}</span>
                          </div>
                          <Badge className="bg-green-500/30 text-green-400">
                            {ranking.reputation} Rep
                          </Badge>
                        </div>
                      ))}
                      
                      {hunterRankings.length === 0 && (
                        <div className="text-center py-8 text-gray-400">
                          <p>No hunters ranked yet</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
              
              {/* Buyer Rankings */}
              <Card className="bg-black/40 border-amber-500/30">
                <CardHeader>
                  <CardTitle className="text-amber-400 flex items-center gap-2">
                    <Crown className="w-5 h-5" />
                    Top Buyers
                  </CardTitle>
                  <CardDescription className="text-amber-200/60">
                    Reputation earned by fulfilling bounty payments
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-96">
                    <div className="space-y-2">
                      {clientRankings.map((ranking) => (
                        <div
                          key={ranking.user_id}
                          className="flex items-center justify-between p-3 bg-amber-500/10 rounded-lg cursor-pointer hover:bg-amber-500/20"
                          onClick={() => setShowUserProfile(ranking.user_id)}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                              ranking.rank === 1 ? 'bg-amber-500 text-black' :
                              ranking.rank === 2 ? 'bg-gray-400 text-black' :
                              ranking.rank === 3 ? 'bg-amber-700 text-white' :
                              'bg-gray-600 text-white'
                            }`}>
                              {ranking.rank}
                            </div>
                            {ranking.avatar ? (
                              <img src={ranking.avatar} alt="" className="w-10 h-10 rounded-full" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-amber-500/30 flex items-center justify-center">
                                <Crown className="w-5 h-5 text-amber-400" />
                              </div>
                            )}
                            <span className="text-white font-medium">{ranking.username}</span>
                          </div>
                          <Badge className="bg-amber-500/30 text-amber-400">
                            {ranking.reputation} Rep
                          </Badge>
                        </div>
                      ))}
                      
                      {clientRankings.length === 0 && (
                        <div className="text-center py-8 text-gray-400">
                          <p>No buyers ranked yet</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      
            <CreateBountyModal
        showCreateBounty={showCreateBounty} setShowCreateBounty={setShowCreateBounty}
        bountyForm={bountyForm} setBountyForm={setBountyForm}
        AREA_BOSSES={AREA_BOSSES} handleCreateBounty={handleCreateBounty} actionLoading={actionLoading}
        formatMesos={formatMesos}
      />

            <CreateExpeditionModal
        showCreateExpedition={showCreateExpedition} setShowCreateExpedition={setShowCreateExpedition}
        expeditionForm={expeditionForm} setExpeditionForm={setExpeditionForm}
        handleCreateExpedition={handleCreateExpedition} actionLoading={actionLoading}
        userCharacters={userCharacters} availableJobs={availableJobs} expeditionBosses={expeditionBosses}
        expeditionWishes={expeditionWishes} formatMesos={formatMesos} hunterLfgPosts={hunterLfgPosts}
        removeJobRequirement={removeJobRequirement} newJobReq={newJobReq} setNewJobReq={setNewJobReq}
        addJobRequirement={addJobRequirement}
      />

            <CreateWishModal
        showCreateWish={showCreateWish} setShowCreateWish={setShowCreateWish}
        wishForm={wishForm} setWishForm={setWishForm} userCharacters={userCharacters}
        handleCreateWish={handleCreateWish} actionLoading={actionLoading} expeditionBosses={expeditionBosses}
        formatMesos={formatMesos} availableJobs={availableJobs}
      />

            <CreateHunterLFGModal
        showCreateLfg={showCreateLfg} setShowCreateLfg={setShowCreateLfg}
        lfgForm={lfgForm} setLfgForm={setLfgForm} handleCreateLfg={handleCreateLfg}
        actionLoading={actionLoading} userCharacters={userCharacters} AREA_BOSSES={AREA_BOSSES}
        expeditionBosses={expeditionBosses} toggleLfgBoss={toggleLfgBoss} toggleLfgCharacter={toggleLfgCharacter}
        availableJobs={availableJobs}
      />

            <ApplyExpeditionModal
        showApplyExpedition={showApplyExpedition} setShowApplyExpedition={setShowApplyExpedition}
        applyForm={applyForm} setApplyForm={setApplyForm} handleApplyExpedition={handleApplyExpedition}
        actionLoading={actionLoading} userCharacters={userCharacters} availableJobs={availableJobs} toggleCharacterSelection={toggleCharacterSelection}
      />

            <ApplyClientModal
        showApplyAsClient={showApplyAsClient} setShowApplyAsClient={setShowApplyAsClient}
        applyForm={applyForm} setApplyForm={setApplyForm} handleApplyAsClient={handleApplyAsClient}
        actionLoading={actionLoading} userCharacters={userCharacters} formatMesos={formatMesos}
        clientApplyIgn={clientApplyIgn} setClientApplyIgn={setClientApplyIgn}
      />

      <ConfirmCompletionModal
        showConfirmDialog={showConfirmDialog} setShowConfirmDialog={setShowConfirmDialog}
        confirmForm={confirmForm} setConfirmForm={setConfirmForm}
        handleConfirmBounty={handleConfirmBounty} handleConfirmExpedition={handleConfirmExpedition}
      />
{/* User Profile Modal */}
      {showUserProfile && (
        <UserProfileModal
          open={!!showUserProfile}
          onOpenChange={(v) => { if (!v) setShowUserProfile(null); }}
          userId={showUserProfile}
        />
      )}
      
      {/* Global Chat (Shoutbox) */}
      <Shoutbox />
      
      {/* Notification Log */}
      <FightNotificationLog
        open={showFightLog}
        onOpenChange={setShowFightLog}
        notifications={fightNotificationHistory}
        onClearAll={handleClearAllLogs}
        onDelete={handleDeleteLog}
      />

      {/* Ko-fi Support */}
      <div className="fixed bottom-4 left-4 z-40">
        <Button
          onClick={() => setShowKofiModal(true)}
          className="bg-[#FF5E5B] hover:bg-[#FF5E5B]/90 text-white shadow-lg rounded-full px-4 py-2"
          size="sm"
        >
          <Heart className="w-4 h-4 mr-1.5 fill-white" />
          Support
        </Button>
        <p className="text-[9px] text-center text-gray-400 mt-0.5">Keep HC running</p>
      </div>
      <KofiModal open={showKofiModal} onOpenChange={setShowKofiModal} />
    </div>
  );
};

export default HuntersChancePage;
